/*
 * business-card/render.js — the same item list, on a canvas.
 *
 * WHY THE PREVIEW SHOWS THE PRINTED COLOUR
 * ────────────────────────────────────────
 * Every colour reaching this file is CMYK, and it is converted to screen RGB by
 * the ink model in ink.js — the simulated press, not the naive formula. So a
 * card that will print as a muted navy looks muted navy in the browser. That is
 * the entire anti-disappointment mechanism (see ink.js); rendering the user's
 * original sRGB here would undo it in one line.
 *
 * WHY IT DRAWS FROM THE SAME LIST AS THE PDF
 * ──────────────────────────────────────────
 * `layout.js` has already decided line breaks, type sizes, bleed expansion and
 * colours. This file only rasterises. Neither renderer gets a vote, so "it
 * looked different in the preview" cannot happen through a layout disagreement
 * — only through a font metric one, and that is bounded and measured
 * (tests/playwright/business-card-metrics.mjs).
 *
 * GUIDES ARE OVERLAY, NEVER CONTENT. The bleed/trim/safe rectangles are drawn
 * after and outside the item list, and the export path never sees them. A guide
 * that can leak into the deliverable is worse than no guide.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CardRender = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    function css(c) {
        var I = window.CardInk;
        if (!c) return '#000';
        var rgb = I.cmykToRgb(c.c, c.m, c.y, c.k);
        return 'rgb(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ')';
    }

    /* The font stack the preview measures AND draws with. One function so a
     * change cannot land in one of the two places. */
    function fontCss(weight, sizePx, family) {
        return (weight >= 600 ? '700 ' : '400 ') + sizePx.toFixed(2) + 'px ' + family;
    }

    /* A measurer for LabelLayout.fitText, in MILLIMETRES.
     *
     * Measuring at a fixed large pixel size and scaling down, rather than at the
     * actual size, keeps the result free of the sub-pixel quantisation some
     * engines apply to small type — at 3 mm ≈ 11 px the rounding is a whole
     * percent, and it lands differently per browser. */
    function measurer(familyFor, pxPerMm) {
        var c = document.createElement('canvas').getContext('2d');
        var REF = 100;                       // px em used for every measurement
        return function (text, style) {
            var fam = familyFor(style);
            c.font = fontCss(style.weight || 400, REF, fam);
            var w = c.measureText(String(text == null ? '' : text)).width / REF;
            var track = (style.track || 0) * String(text || '').length;
            return (w + track) * (style.size || 3);
        };
    }

    /* ── drawing ────────────────────────────────────────────────────────────
     * `s` is px per mm. Origin of the canvas is the top-left of the BLEED box,
     * so trim-space coordinates are offset by the bleed. Passing the offset in
     * rather than baking it lets the same function draw a trimmed preview and a
     * with-bleed proof.
     */
    function draw(ctx, items, opt) {
        var s = opt.scale;
        var ox = opt.originX || 0, oy = opt.originY || 0;
        var X = function (mm) { return (ox + mm) * s; };
        var Y = function (mm) { return (oy + mm) * s; };

        for (var i = 0; i < items.length; i++) {
            var it = items[i];
            ctx.save();
            switch (it.type) {
                case 'rect':
                    ctx.fillStyle = css(it.color);
                    if (it.radius > 0.01) roundRect(ctx, X(it.x), Y(it.y), it.w * s, it.h * s, it.radius * s), ctx.fill();
                    else ctx.fillRect(X(it.x), Y(it.y), it.w * s, it.h * s);
                    break;
                case 'frame':
                    ctx.strokeStyle = css(it.color);
                    ctx.lineWidth = Math.max(0.5, it.stroke * s);
                    ctx.strokeRect(X(it.x), Y(it.y), it.w * s, it.h * s);
                    break;
                case 'poly':
                    ctx.fillStyle = css(it.color);
                    ctx.beginPath();
                    it.points.forEach(function (p, k) {
                        k ? ctx.lineTo(X(p[0]), Y(p[1])) : ctx.moveTo(X(p[0]), Y(p[1]));
                    });
                    ctx.closePath(); ctx.fill();
                    break;
                case 'line':
                    ctx.strokeStyle = css(it.color);
                    ctx.lineWidth = Math.max(0.5, it.stroke * s);
                    ctx.beginPath();
                    ctx.moveTo(X(it.x), Y(it.y));
                    ctx.lineTo(X(it.x + it.w), Y(it.y + (it.h || 0)));
                    ctx.stroke();
                    break;
                case 'icon':
                    drawIcon(ctx, it, X, Y, s);
                    break;
                case 'text':
                    drawText(ctx, it, X, Y, s, opt);
                    break;
                case 'photo':
                    drawPhoto(ctx, it, X, Y, s, opt);
                    break;
                case 'image':
                    drawImage(ctx, it, X, Y, s, opt);
                    break;
                case 'qr':
                    drawQr(ctx, it, X, Y, s, opt);
                    break;
                case 'path':
                    drawPath(ctx, it, X, Y, s);
                    break;
            }
            ctx.restore();
        }
    }

    /* A free vector path in TRIM millimetres — the ornaments /gift-certificate
     * borrows from certificate-maker (frames.js, backgrounds.js). Fill and
     * stroke are CMYK like everything else here; `opacity` is the only
     * transparency the PDF side can also express (an ExtGState), so it is the
     * only one offered. */
    function drawPath(ctx, it, X, Y, s) {
        if (!it.d) return;
        var p = new Path2D(it.d);
        ctx.translate(X(0), Y(0));
        ctx.scale(s, s);
        if (it.opacity != null) ctx.globalAlpha = Math.max(0, Math.min(1, it.opacity));
        if (it.fill) { ctx.fillStyle = css(it.fill); ctx.fill(p, it.evenodd ? 'evenodd' : 'nonzero'); }
        if (it.stroke && it.sw > 0) {
            ctx.strokeStyle = css(it.stroke);
            ctx.lineWidth = it.sw;
            ctx.lineCap = it.cap || 'butt';
            ctx.lineJoin = it.join || 'miter';
            ctx.stroke(p);
        }
    }

    function roundRect(ctx, x, y, w, h, r) {
        r = Math.min(r, w / 2, h / 2);
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
    }

    /* Stroked, matching the exporter. Round caps and joins are not decoration:
       the same path stroked with butt caps in one backend and round in the other
       reads as a different icon at 2.6 mm, which is precisely the size these are
       drawn at. */
    function drawIcon(ctx, it, X, Y, s) {
        var p = new Path2D(it.path);
        ctx.save();
        ctx.translate(X(it.x), Y(it.y));
        var k = (it.size * s) / 24;
        ctx.scale(k, k);
        ctx.strokeStyle = css(it.color);
        ctx.lineWidth = (it.stroke || it.size * 0.07) / (it.size / 24);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke(p);
        ctx.restore();
    }

    var _lsOk = null;
    function supportsLetterSpacing(ctx) {
        if (_lsOk === null) _lsOk = (typeof ctx.letterSpacing === 'string');
        return _lsOk;
    }

    function drawText(ctx, it, X, Y, s, opt) {
        var fam = opt.familyFor({ weight: it.weight, face: it.face });
        ctx.fillStyle = css(it.color);
        ctx.font = fontCss(it.weight, it.size * s, fam);
        ctx.textBaseline = 'alphabetic';
        /* ⚠️ ANCHOR THE LEFT EDGE EXPLICITLY. `start` means the RIGHT edge once
           the direction below is right-to-left, and every x computed here is a
           left edge. */
        ctx.textAlign = 'left';
        for (var li = 0; li < it.lines.length; li++) {
            var line = it.lines[li];
            if (!line) continue;
            /* ⚠️ THE CANVAS DOES NOT GUESS THE PARAGRAPH DIRECTION — it takes
             * it from the element, so an Arabic line on a left-to-right page is
             * laid out as a left-to-right paragraph. That is not a subtlety:
             * «الرياض ABC 2026» came out Arabic-then-Latin here and
             * Latin-then-Arabic in the PDF, which is the correct order. Ask the
             * exporter's own rule so the two cannot disagree. */
            var rtlPara = !!(window.PdfTextFont && window.PdfTextFont.baseIsRtl
                && window.PdfTextFont.baseIsRtl(line));
            try { ctx.direction = rtlPara ? 'rtl' : 'ltr'; } catch (e) {}
            var w = ctx.measureText(line).width;
            var track = (it.track || 0) * it.size * s * line.length;
            var total = w + track;
            var x = X(it.x);
            if (it.align === 'center') x = X(it.x) + (it.w * s - total) / 2;
            else if (it.align === 'right') x = X(it.x) + it.w * s - total;
            var y = Y(it.y + li * it.lineHeight);
            if (!it.track) {
                ctx.fillText(line, x, y);
            } else if (rtlPara || supportsLetterSpacing(ctx)) {
                /* ⚠️ NEVER STEP A RIGHT-TO-LEFT LINE GLYPH BY GLYPH. Arabic
                 * letters take their shape from their neighbours; drawing each
                 * one alone asks the shaper to shape it alone, and every letter
                 * comes out in its isolated form — the word falls apart. Where
                 * the engine has `letterSpacing` it does the same job without
                 * breaking the joins. */
                var prev = ctx.letterSpacing;
                try { ctx.letterSpacing = ((it.track || 0) * it.size * s).toFixed(3) + 'px'; } catch (e) {}
                ctx.fillText(line, x, y);
                try { ctx.letterSpacing = prev || '0px'; } catch (e) {}
            } else {
                /* Canvas has letterSpacing only in newer engines; stepping the
                 * glyphs keeps tracked type identical across browsers, and
                 * tracked type is most of what a card's small caps are. */
                var step = (it.track || 0) * it.size * s;
                var cx = x;
                for (var ci = 0; ci < line.length; ci++) {
                    ctx.fillText(line[ci], cx, y);
                    cx += ctx.measureText(line[ci]).width + step;
                }
            }
        }
    }

    /* The background is handed here ALREADY BAKED — blurred, veiled, tinted and
     * converted to print colour by the host, at the size of this canvas. This
     * file does not know what a blur is, which is the point: the exporter bakes
     * the same way at print resolution, and neither renderer can drift from the
     * other by owning a copy of the effect chain. */
    function drawPhoto(ctx, it, X, Y, s, opt) {
        var cv = opt.photos && opt.photos[it.pkey];
        if (!cv) return;
        ctx.drawImage(cv, X(it.x), Y(it.y), it.w * s, it.h * s);
    }

    function drawImage(ctx, it, X, Y, s, opt) {
        /* A source is an <img> (not drawn until `complete`) or an already decoded
         * canvas / ImageBitmap, which has no `complete` at all: a tool that redraws
         * on every keystroke hands over the decoded copy, because WebKit may drop
         * an off-DOM image's decoded pixels and decode the file again per drawImage. */
        var img = opt.images && opt.images[it.src];
        if (!img || img.complete === false) return;
        if (it.mono && it.color) {
            /* Preview of the stencil export: the logo silhouette in the ink
             * colour. Drawn through a scratch canvas because `source-in` on the
             * live context would clip everything already painted. */
            var w = Math.max(1, Math.round(it.w * s)), h = Math.max(1, Math.round(it.h * s));
            var tmp = document.createElement('canvas');
            tmp.width = w; tmp.height = h;
            var tc = tmp.getContext('2d');
            tc.drawImage(img, 0, 0, w, h);
            tc.globalCompositeOperation = 'source-in';
            tc.fillStyle = css(it.color);
            tc.fillRect(0, 0, w, h);
            ctx.drawImage(tmp, X(it.x), Y(it.y), it.w * s, it.h * s);
            return;
        }
        /* The PRINT simulation when one has been built, the raw file when it
         * has not. The palette already previews as it will print; a logo that
         * did not was the last place on the card where the screen and the paper
         * disagreed — measured 2026-08-23, nine of eighteen real brand colours
         * shift perceptibly, and Ozon blue (#005bff) lands on #0d77c6. */
        var sim = opt.printSim && opt.printSim[it.src];
        ctx.drawImage(sim || img, X(it.x), Y(it.y), it.w * s, it.h * s);
    }

    function drawQr(ctx, it, X, Y, s, opt) {
        var m = opt.qrMatrix && opt.qrMatrix(it.payload, it.ecc);
        if (!m) return;
        var units = m.n + 8;
        var cell = (it.size * s) / units;
        ctx.fillStyle = css(it.light);
        ctx.fillRect(X(it.x), Y(it.y), it.size * s, it.size * s);
        ctx.fillStyle = css(it.dark);
        for (var r = 0; r < m.n; r++) {
            var c = 0;
            while (c < m.n) {
                if (!m.rows[r][c]) { c++; continue; }
                var start = c;
                while (c < m.n && m.rows[r][c]) c++;
                /* Ceil the run width so neighbouring runs overlap by a fraction
                 * of a pixel. Without it the seams between runs show as light
                 * hairlines at some zoom levels and the preview looks like a
                 * damaged symbol. */
                ctx.fillRect(
                    X(it.x) + (start + 4) * cell,
                    Y(it.y) + (r + 4) * cell,
                    Math.ceil((c - start) * cell) + 0.5,
                    Math.ceil(cell) + 0.5);
            }
        }
    }

    /* ── guides ─────────────────────────────────────────────────────────────
     * Three rectangles that explain the three failure modes at a glance: the
     * bleed the printer trims into, the line the guillotine aims at, and the
     * margin content must stay inside. Dashed and low-contrast on purpose —
     * they are annotation, and a guide as loud as the design makes the design
     * impossible to judge.
     */
    function drawGuides(ctx, boxes, opt) {
        var s = opt.scale, b = boxes.bleed;
        ctx.save();
        ctx.setLineDash([4, 3]);
        ctx.lineWidth = 1;

        if (opt.showBleed && b > 0) {
            ctx.strokeStyle = 'rgba(255,90,90,0.75)';
            ctx.strokeRect(0.5, 0.5, (boxes.w + 2 * b) * s - 1, (boxes.h + 2 * b) * s - 1);
        }
        if (opt.showTrim) {
            ctx.strokeStyle = 'rgba(60,140,255,0.9)';
            ctx.setLineDash([]);
            ctx.strokeRect(b * s, b * s, boxes.w * s, boxes.h * s);
        }
        if (opt.showSafe) {
            ctx.setLineDash([4, 3]);
            ctx.strokeStyle = 'rgba(90,220,140,0.8)';
            ctx.strokeRect((b + boxes.safe) * s, (b + boxes.safe) * s,
                (boxes.w - 2 * boxes.safe) * s, (boxes.h - 2 * boxes.safe) * s);
        }
        ctx.restore();
    }

    /* Clip to the trim box with the chosen corner radius, so the preview shows
     * the card as it will be held — including a round-corner die eating a logo
     * corner, which is invisible until someone clips for it. */
    function clipCard(ctx, boxes, opt) {
        var s = opt.scale, b = opt.withBleed ? boxes.bleed : 0;
        var r = (opt.radius || 0) * s;
        ctx.beginPath();
        if (r > 0.5) roundRect(ctx, b * s, b * s, boxes.w * s, boxes.h * s, r);
        else ctx.rect(b * s, b * s, boxes.w * s, boxes.h * s);
        ctx.clip();
    }

    return {
        css: css,
        fontCss: fontCss,
        measurer: measurer,
        draw: draw,
        drawGuides: drawGuides,
        clipCard: clipCard,
        roundRect: roundRect,
    };
});
